import { useEffect, useState, type FormEvent } from "react";
import { api } from "../lib/api";

interface LogEntry {
  id: number;
  username: string | null;
  category: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  target_name: string | null;
  details_json: unknown;
  ip_address: string | null;
  created_at: string;
}

interface LogResponse {
  items: LogEntry[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface LogFilters {
  category: string;
  username: string;
  action: string;
  target: string;
  ip: string;
  from: string;
  to: string;
}

const emptyFilters: LogFilters = { category: "", username: "", action: "", target: "", ip: "", from: "", to: "" };
const categories = ["", "SYSTEM", "LOGIN", "USER", "RTSP", "LIVE", "RECORD", "PLAYBACK", "STORAGE", "DOWNLOAD"];
const categoryLabels: Record<string, string> = {
  SYSTEM: "시스템", LOGIN: "로그인", USER: "사용자", RTSP: "카메라", LIVE: "실시간",
  RECORD: "녹화", PLAYBACK: "재생", STORAGE: "저장소", DOWNLOAD: "내보내기"
};

export function LogsPage() {
  const [result, setResult] = useState<LogResponse>({ items: [], total: 0, page: 1, limit: 50, totalPages: 1 });
  const [filters, setFilters] = useState<LogFilters>(emptyFilters);
  const [appliedQuery, setAppliedQuery] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const load = async (selectedPage = page, query = appliedQuery) => {
    setLoading(true);
    setMessage("");
    try {
      const suffix = query ? `&${query}` : "";
      setResult(await api<LogResponse>(`/api/logs?page=${selectedPage}&limit=50${suffix}`));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "로그 조회 실패");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(page, appliedQuery); }, [page, appliedQuery]);

  const applyFilters = (event: FormEvent) => {
    event.preventDefault();
    const params = new URLSearchParams();
    for (const key of ["category", "username", "action", "target", "ip"] as const) {
      if (filters[key].trim()) params.set(key, filters[key].trim());
    }
    if (filters.from) params.set("from", seoulInputToIso(filters.from));
    if (filters.to) params.set("to", seoulInputToIso(filters.to));
    setPage(1);
    setAppliedQuery(params.toString());
  };

  const resetFilters = () => {
    setFilters(emptyFilters);
    setPage(1);
    setAppliedQuery("");
  };

  return <>
    <div className="page-heading">
      <div><p className="eyebrow">작업 및 장애 기록</p><h2>시스템 로그</h2></div>
      <button className="btn btn-sm btn-outline-light" onClick={() => void load()} disabled={loading}><i className="bi bi-arrow-clockwise me-1" />새로 고침</button>
    </div>

    <form className="panel log-filter-panel mb-3" onSubmit={applyFilters}>
      <div className="log-filter-grid">
        <div><label htmlFor="log-category">분류</label><select id="log-category" className="form-select form-select-sm" value={filters.category} onChange={(event) => setFilters({ ...filters, category: event.target.value })}>{categories.map((item) => <option value={item} key={item}>{item ? categoryLabels[item] ?? item : "전체 분류"}</option>)}</select></div>
        <div><label htmlFor="log-username">사용자</label><input id="log-username" className="form-control form-control-sm" value={filters.username} onChange={(event) => setFilters({ ...filters, username: event.target.value })} placeholder="계정" /></div>
        <div><label htmlFor="log-action">작업</label><input id="log-action" className="form-control form-control-sm" value={filters.action} onChange={(event) => setFilters({ ...filters, action: event.target.value })} placeholder="예: playback" /></div>
        <div><label htmlFor="log-target">대상</label><input id="log-target" className="form-control form-control-sm" value={filters.target} onChange={(event) => setFilters({ ...filters, target: event.target.value })} placeholder="카메라 이름" /></div>
        <div><label htmlFor="log-ip">IP</label><input id="log-ip" className="form-control form-control-sm" value={filters.ip} onChange={(event) => setFilters({ ...filters, ip: event.target.value })} placeholder="접속 IP" /></div>
        <div><label htmlFor="log-from">시작 시각</label><input id="log-from" type="datetime-local" step="1" className="form-control form-control-sm" value={filters.from} onChange={(event) => setFilters({ ...filters, from: event.target.value })} /></div>
        <div><label htmlFor="log-to">종료 시각</label><input id="log-to" type="datetime-local" step="1" className="form-control form-control-sm" value={filters.to} onChange={(event) => setFilters({ ...filters, to: event.target.value })} /></div>
      </div>
      <div className="d-flex gap-2 mt-3"><button type="submit" className="btn btn-sm btn-primary" disabled={loading}>조회</button><button type="button" className="btn btn-sm btn-outline-secondary" onClick={resetFilters}>초기화</button></div>
    </form>

    {message && <div className="alert alert-danger">{message}</div>}
    <div className="panel">
      <div className="panel-title"><h3>로그 {result.total.toLocaleString("ko-KR")}건</h3><span>최신순 · 페이지당 50건</span></div>
      <div className="table-responsive"><table className="table table-dark table-hover align-middle mb-0"><thead><tr><th>시간</th><th>분류</th><th>사용자</th><th>작업</th><th>대상</th><th>상세</th><th>IP</th></tr></thead><tbody>
        {result.items.map((log) => <tr key={log.id}>
          <td className="text-nowrap">{new Date(log.created_at).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" })}</td>
          <td><span className="badge text-bg-secondary">{categoryLabels[log.category] ?? log.category}</span></td>
          <td>{log.username ?? "시스템"}</td>
          <td><code>{log.action}</code></td>
          <td><span className="log-target-name">{log.target_name ?? "-"}</span>{log.target_type && <small>{targetTypeLabel(log.target_type)}</small>}</td>
          <td>{log.details_json ? <details className="log-detail"><summary>상세 보기</summary><code className="log-details">{formatDetails(log.details_json)}</code></details> : "-"}</td>
          <td className="text-nowrap">{log.ip_address ?? "-"}</td>
        </tr>)}
        {!loading && !result.items.length && <tr><td colSpan={7} className="empty-state">조건에 맞는 로그가 없습니다.</td></tr>}
      </tbody></table></div>
      <div className="log-pagination mt-3">
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page <= 1 || loading} onClick={() => setPage((current) => current - 1)}><i className="bi bi-chevron-left me-1" />이전</button>
        <span>{result.page.toLocaleString("ko-KR")} / {result.totalPages.toLocaleString("ko-KR")}</span>
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page >= result.totalPages || loading} onClick={() => setPage((current) => current + 1)}>다음<i className="bi bi-chevron-right ms-1" /></button>
      </div>
    </div>
  </>;
}

function seoulInputToIso(value: string): string {
  return new Date(`${value}${value.length === 16 ? ":00" : ""}+09:00`).toISOString();
}

function formatDetails(value: unknown): string {
  if (typeof value !== "string") return JSON.stringify(value);
  try { return JSON.stringify(JSON.parse(value)); }
  catch { return value; }
}

function targetTypeLabel(value: string): string {
  return ({ camera: "카메라", recording_segment: "녹화 파일", recording_export: "내보내기", user: "사용자", role: "권한 역할" } as Record<string, string>)[value] ?? value;
}
